import { Box } from '@chakra-ui/react';
import { RECURRING_SINGLE_PAYMENT } from '@melio/ap-domain';
import { LargeFileUpload, SplitScreen, useTheme, useToast } from '@melio/penny';
import { Traits, useAnalytics, withAnalyticsContext } from '@melio/platform-analytics';
import { PostBillsRequest, useAccountingPlatforms, useAccountsWithLogoUrl, useBills } from '@melio/platform-api';
import { useDateUtils } from '@melio/platform-utils';
import { useEffect, useState } from 'react';

import { NewBillHeader } from '../../add-bill/AddBillV2Form/components/NewBillHeader';
import { AddInstantBillV2FormActivityFields } from '../AddInstantBillV2Form/types';
import { AddInstantBillV2Screen } from './screens/AddInstantBillV2.screen';
import { AddInstantBill2RecurringEndBy, AddInstantBillV2SubmitEnum, AddInstantBillV2SubmitType } from './types';
import { createPayloadByAccountingSoftware } from './utils';

export type AddInstantBillV2ActivityProps = {
  defaultValues?: Partial<AddInstantBillV2FormActivityFields>;
  isRecurringPaymentsEnabled?: boolean;
  isInternationalSupportedForRecurringFlow?: boolean;
  onDone: ({ data, billId }: { data?: AddInstantBillV2FormActivityFields; billId?: string }) => void;
  onClose: VoidFunction;
  onGoToDashboard: VoidFunction;
  onGoToAddNewBill: (params: {
    file?: File | null;
    amount?: string;
    vendorId?: string;
    categoryId?: string;
    isAdvancedView?: boolean;
  }) => void;
  onError?: ErrorFunction;
};

export const AddInstantBillV2Activity = withAnalyticsContext<AddInstantBillV2ActivityProps>(
  ({
    setAnalyticsProperties,
    defaultValues,
    isRecurringPaymentsEnabled,
    isInternationalSupportedForRecurringFlow,
    onDone,
    onClose,
    onError,
    onGoToAddNewBill,
    onGoToDashboard,
  }) => {
    const {
      activeAccountingPlatform,
      isFetching: isFetchingAccountingPlatforms,
      hasAccountingPlatform,
    } = useAccountingPlatforms();
    const { isFetching: isFetchingAccounts } = useAccountsWithLogoUrl({ enabled: false });
    const [isRecurringFrequency, setIsRecurringFrequency] = useState<boolean>(false);
    const theme = useTheme();
    const { create: createBill, isMutating: isSaving } = useBills({ enabled: false });
    const toast = useToast();
    const { createDate } = useDateUtils();
    const today = createDate();
    const { setTraits, track, trackMarketing } = useAnalytics();
    const setBillTraits = () => {
      const traits: Traits = {
        create_a_bill: true,
        last_bill_added_date: createDate().toISOString(),
      };

      setTraits(traits);
    };

    const handleFail = (error: PlatformError) => {
      toast({ type: 'error', title: error.message || 'Failed to create the bill' });
      onError?.(error);
    };

    setAnalyticsProperties({
      PageName: 'bill-details',
      Flow: 'simplified',
      Intent: 'add-bill',
      IsSynced: !!hasAccountingPlatform,
      LineItems: true,
    });

    useEffect(() => {
      track('Bill', 'View');
    }, [track]);

    const handleDone = async (data: AddInstantBillV2FormActivityFields, target?: AddInstantBillV2SubmitType) => {
      try {
        if (data.paymentFrequency === RECURRING_SINGLE_PAYMENT) {
          const amount = parseFloat(data.billAmount);
          const billCreationPayload: PostBillsRequest = createPayloadByAccountingSoftware(
            {
              vendorId: data.vendorId,
              categoryId: data.categoryId,
              amount,
              today,
              invoiceNumber: data.invoiceNumber,
            },
            activeAccountingPlatform
          );

          const createdBill = await createBill(billCreationPayload);
          const analyticsProperties = {
            BillId: createdBill.id,
            Amount: amount,
            Cta: target === AddInstantBillV2SubmitEnum.CONTINUE_TO_PAY ? 'continue-to-pay' : 'save-and-close',
          };
          track('Bill', 'Click', analyticsProperties);
          trackMarketing('bill-create_save-success', analyticsProperties);
          setBillTraits();

          if (target === AddInstantBillV2SubmitEnum.SAVE_AND_CLOSE) {
            onGoToDashboard();
          } else {
            onDone({ billId: createdBill.id });
          }
        } else {
          track('Bill', 'Click', {
            amount: data?.billAmount,
            PaymentFrequency: data?.paymentFrequency,
            RecurringStartDate: data?.startDate,
            RecurringPaymentEndBy: data?.endPolicy,
            RecurringPaymentEndValue:
              data?.endPolicy === AddInstantBill2RecurringEndBy.DATE
                ? data?.endDate?.toISOString()
                : data?.numOfOccurrences,
            Cta: target === AddInstantBillV2SubmitEnum.CONTINUE_TO_PAY ? 'continue-to-pay' : 'save-and-close',
          });

          onDone({ data });
        }
      } catch (error) {
        handleFail(error as PlatformError);
      }
    };

    const onSetFile = (file: File | null) => {
      onGoToAddNewBill({
        file,
      });
    };

    const onAdvanceBilldetails = (formValues?: AddInstantBillV2FormActivityFields) => {
      onGoToAddNewBill({
        vendorId: formValues?.vendorId,
        amount: formValues?.billAmount,
        categoryId: formValues?.categoryId,
        isAdvancedView: true,
      });
    };

    const handleClose = () => {
      track('Bill', 'Click', {
        Cta: 'exit',
      });
      onClose();
    };

    const largeFileUploadProps = { isDisabled: isRecurringFrequency };

    return (
      <SplitScreen
        shouldAllowResizing
        header={<NewBillHeader onClose={handleClose} />}
        isLoading={isFetchingAccountingPlatforms || isFetchingAccounts}
        panelA={{
          maxWidth: 34,
          padding: { xs: 'xxl', m: 'xl' },
          content: (
            <LargeFileUpload onChange={onSetFile} acceptTypes={['pdf', 'png', 'jpg']} {...largeFileUploadProps} />
          ),
        }}
        panelB={{
          padding: 'none',
          content: (
            <Box background={theme.colors.neutral.white} pt={{ xs: 'xl', m: 'xxl' }} px="xxl" h="100%">
              <AddInstantBillV2Screen
                defaultValues={defaultValues}
                isSaving={isSaving}
                activeAccountingPlatform={activeAccountingPlatform}
                isRecurringPaymentsEnabled={isRecurringPaymentsEnabled}
                isInternationalSupportedForRecurringFlow={isInternationalSupportedForRecurringFlow}
                isRecurringFrequency={isRecurringFrequency}
                onUpdateFrequency={setIsRecurringFrequency}
                onDone={(data, _event, target) => handleDone(data, target)}
                onAdvanceBilldetails={onAdvanceBilldetails}
                onClose={handleClose}
              />
            </Box>
          ),
        }}
      />
    );
  }
);
